/**
 * Phase 1 Item 9 — per-study DoA responsibility subset panel.
 *
 * Compact card on the Study Workspace Hub that summarises how many
 * responsibilities the study has opted into (or "All active
 * responsibilities" when no opt-in is configured — the safe default).
 * Click "Configure" to open the multi-select modal.
 *
 * Read for anyone who can view the study; write for site_study_designer
 * / site_admin / regulatory_specialist / platform tier only. The write
 * roles match the PUT handler's requireRole so the button hides for
 * viewers who couldn't submit anyway.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ClipboardList, Loader2, Plus, Search, X } from 'lucide-react';
import {
  adminDoa,
  studyDoaResponsibilities,
  type StudyDoaResponsibilitiesResponse,
} from '@/api/client';
import { useHasRole } from '@/components/RoleGate';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { UnsavedChangesModal } from '@/components/ui/UnsavedChangesModal';

const WRITE_ROLES = [
  'site_study_designer',
  'regulatory_specialist',
] as const;

// Roles the createResponsibility endpoint accepts (handlers/admin/doa/
// createResponsibility.ts). Adding to the catalog is master data, which is
// Platform Admin only (Harish, 1 Oct 2026); the others can still choose the
// study's subset with the Configure button.
const CATALOG_CREATE_ROLES = [
  'platform_admin',
] as const;

function slugifyCode(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function DoaResponsibilitiesPanel({ studyId, readOnly = false }: { studyId: string; readOnly?: boolean }) {
  // The grid decides; WRITE_ROLES is the fallback before it loads. Needed
  // once this panel appears in the onboarding wizard: WRITE_ROLES never
  // named the platform admins, who are exactly who runs onboarding.
  const perms = useMyPermissions();
  const canWrite =
    perms.holdsOperation('studies.doa_responsibilities.write', WRITE_ROLES) && !readOnly;
  const [modalOpen, setModalOpen] = useState(false);

  const q = useQuery({
    queryKey: ['study', studyId, 'doa-responsibilities'],
    queryFn: () => studyDoaResponsibilities.get(studyId),
    enabled: !!studyId,
  });

  const data = q.data;
  const summary = data
    ? data.is_default
      ? `All active responsibilities (${data.catalog.length})`
      : `${data.subset.length} of ${data.catalog.length} responsibilities`
    : '—';

  return (
    <section className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-brand-500" />
          <div>
            <h2 className="text-sm font-semibold text-ink-primary">DoA responsibilities</h2>
            <p className="text-xs text-ink-muted">Controls which columns appear on this study's DoA grid.</p>
          </div>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            disabled={q.isLoading}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50 disabled:opacity-50"
          >
            Configure
          </button>
        )}
      </div>
      <div className="mt-3 text-xs text-ink-secondary">
        {q.isLoading ? (
          <span className="flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</span>
        ) : (
          summary
        )}
      </div>

      {modalOpen && data && (
        <ConfigureModal
          studyId={studyId}
          initial={data}
          onClose={() => setModalOpen(false)}
        />
      )}
    </section>
  );
}

function ConfigureModal({
  studyId,
  initial,
  onClose,
}: {
  studyId: string;
  initial: StudyDoaResponsibilitiesResponse;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const canCreateCatalog = useHasRole(CATALOG_CREATE_ROLES);
  // Local copy so a just-created responsibility appears in the list
  // without waiting for the panel query to refetch.
  const [catalog, setCatalog] = useState(initial.catalog);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initial.subset));
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newCode, setNewCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [newDefinition, setNewDefinition] = useState('');

  // Sort catalog for a stable display. Codes in the subset that are
  // NOT in the current active catalog show up as "retired" chips so
  // the designer knows to clean them up.
  const activeCodes = useMemo(() => new Set(catalog.map((c) => c.code)), [catalog]);
  const retiredCodes = useMemo(
    () => initial.subset.filter((c) => !activeCodes.has(c)),
    [initial.subset, activeCodes],
  );

  const filteredCatalog = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return catalog;
    return catalog.filter((c) =>
      `${c.label} ${c.code} ${c.definition}`.toLowerCase().includes(term),
    );
  }, [catalog, search]);

  const create = useMutation({
    mutationFn: () => {
      const label = newLabel.trim();
      const code = (codeTouched ? newCode.trim() : slugifyCode(label)) || slugifyCode(label);
      return adminDoa.createResponsibility({
        code,
        label,
        definition: newDefinition.trim(),
        sort_order: catalog.reduce((max, c) => Math.max(max, c.sort_order), 0) + 1,
        is_active: true,
      });
    },
    onSuccess: ({ responsibility }) => {
      setCatalog((prev) =>
        prev.some((c) => c.code === responsibility.code)
          ? prev
          : [
            ...prev,
            {
              responsibility_id: responsibility.responsibility_id,
              code: responsibility.code,
              label: responsibility.label,
              definition: responsibility.definition,
              sort_order: responsibility.sort_order,
            },
          ],
      );
      // Only auto-select when a subset is already being curated. In default
      // mode (empty subset) the new code is inherited anyway, and selecting
      // just it would wrongly narrow the study to a single responsibility.
      setSelected((prev) => (prev.size > 0 ? new Set(prev).add(responsibility.code) : prev));
      toast.success(`Added "${responsibility.label}" to the catalog.`);
      qc.invalidateQueries({ queryKey: ['study', studyId, 'doa-responsibilities'] });
      qc.invalidateQueries({ queryKey: ['doa-grid'] });
      setShowAdd(false);
      setNewLabel('');
      setNewCode('');
      setCodeTouched(false);
      setNewDefinition('');
    },
    onError: (e: Error) => toast.error(`Could not add responsibility: ${e.message}`),
  });

  const initialSet = useMemo(() => new Set(initial.subset), [initial.subset]);
  const isDirty = useMemo(() => {
    if (selected.size !== initialSet.size) return true;
    for (const c of selected) if (!initialSet.has(c)) return true;
    return false;
  }, [selected, initialSet]);

  const unsavedGuard = useUnsavedChangesGuard(isDirty);

  const save = useMutation({
    mutationFn: () =>
      studyDoaResponsibilities.put(studyId, { codes: [...selected] }),
    onSuccess: (r) => {
      toast.success(
        r.is_default
          ? 'Cleared — study inherits the full active catalog.'
          : `Saved ${r.codes.length} responsibility opt-in${r.codes.length === 1 ? '' : 's'}.`,
      );
      qc.invalidateQueries({ queryKey: ['study', studyId, 'doa-responsibilities'] });
      // Grid columns depend on this — invalidate every open DoA grid.
      qc.invalidateQueries({ queryKey: ['doa-grid'] });
      onClose();
    },
    onError: (e: Error) => toast.error(`Save failed: ${e.message}`),
  });

  function toggle(code: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  function selectAll() {
    setSelected(new Set(catalog.map((c) => c.code)));
  }

  function clearAll() {
    setSelected(new Set());
  }

  useEffect(() => {
    // Prevent background scroll while modal is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
      aria-labelledby="doa-resp-title"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative flex max-h-[85vh] w-full max-w-lg flex-col rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-center justify-between border-b border-surface-border px-4 py-3">
          <div>
            <h3 id="doa-resp-title" className="text-sm font-semibold text-ink-primary">
              Configure DoA responsibilities
            </h3>
            <p className="text-[11px] text-ink-muted">
              Pick which responsibilities appear as columns on this study's DoA grid. Leave every
              box unchecked to inherit the full active catalog.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex items-center justify-between border-b border-surface-border px-4 py-2 text-[11px] text-ink-muted">
          <span>
            {selected.size} of {catalog.length} selected
            {selected.size === 0 && ' — inherits full catalog'}
          </span>
          <div className="flex gap-3">
            <button type="button" onClick={selectAll} className="text-accent-700 hover:underline">
              Select all
            </button>
            <button type="button" onClick={clearAll} className="text-accent-700 hover:underline">
              Clear
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 border-b border-surface-border px-4 py-2">
          <div className="flex flex-1 items-center gap-2 rounded border border-surface-border bg-surface-muted/30 px-2 h-8">
            <Search className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search responsibilities…"
              className="flex-1 bg-transparent text-xs text-ink-primary outline-none placeholder:text-ink-muted"
            />
          </div>
          {canCreateCatalog && !showAdd && (
            <button
              type="button"
              onClick={() => {
                setShowAdd(true);
                setNewCode(slugifyCode(newLabel));
              }}
              className="inline-flex items-center gap-1 rounded border border-surface-border px-2.5 h-8 text-xs font-medium hover:bg-surface-muted/50"
            >
              <Plus className="h-3.5 w-3.5" /> Add new
            </button>
          )}
        </div>

        {showAdd && (
          <div className="border-b border-surface-border bg-surface-muted/20 px-4 py-3">
            <div className="space-y-2">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
                  Label
                </label>
                <input
                  type="text"
                  value={newLabel}
                  onChange={(e) => {
                    setNewLabel(e.target.value);
                    if (!codeTouched) setNewCode(slugifyCode(e.target.value));
                  }}
                  placeholder="e.g. Administer study intervention"
                  maxLength={255}
                  className="mt-0.5 w-full rounded border border-surface-border bg-surface-card px-2 py-1 text-xs text-ink-primary outline-none focus:border-accent-500"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
                  Code
                </label>
                <input
                  type="text"
                  value={newCode}
                  onChange={(e) => {
                    setCodeTouched(true);
                    setNewCode(e.target.value);
                  }}
                  placeholder="lowercase-kebab-case"
                  maxLength={64}
                  className="mt-0.5 w-full rounded border border-surface-border bg-surface-card px-2 py-1 font-mono text-xs text-ink-primary outline-none focus:border-accent-500"
                />
                <p className="mt-0.5 text-[10px] text-ink-muted">
                  Immutable once created. Lowercase letters, numbers and hyphens.
                </p>
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
                  Definition (optional)
                </label>
                <textarea
                  value={newDefinition}
                  onChange={(e) => setNewDefinition(e.target.value)}
                  rows={2}
                  className="mt-0.5 w-full rounded border border-surface-border bg-surface-card px-2 py-1 text-xs text-ink-primary outline-none focus:border-accent-500"
                />
              </div>
              <div className="flex justify-end gap-2 pt-0.5">
                <button
                  type="button"
                  onClick={() => setShowAdd(false)}
                  className="rounded border border-surface-border px-2.5 py-1 text-xs font-medium hover:bg-surface-muted/50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => create.mutate()}
                  disabled={
                    create.isPending || !newLabel.trim() || slugifyCode(newLabel).length < 2
                  }
                  className="inline-flex items-center gap-1 rounded bg-accent-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
                >
                  {create.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                  Add to catalog
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-3">
          <ul className="space-y-1.5">
            {filteredCatalog.length === 0 && (
              <li className="px-1.5 py-4 text-center text-[11px] text-ink-muted">
                No responsibilities match "{search}".
              </li>
            )}
            {filteredCatalog.map((r) => {
              const checked = selected.has(r.code);
              return (
                <li key={r.code}>
                  <label className="flex cursor-pointer items-start gap-2 rounded p-1.5 hover:bg-surface-muted/40">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(r.code)}
                      className="mt-0.5 h-3.5 w-3.5"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-ink-primary">{r.label}</div>
                      {r.definition && (
                        <div className="mt-0.5 text-[11px] text-ink-muted">{r.definition}</div>
                      )}
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>

          {retiredCodes.length > 0 && (
            <div className="mt-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
              <div className="font-semibold">Retired responsibilities on file:</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {retiredCodes.map((c) => (
                  <span key={c} className="rounded bg-white/70 px-1.5 py-0.5 font-mono">
                    {c}
                  </span>
                ))}
              </div>
              <div className="mt-1">
                These codes are no longer in the active catalog. Save to drop them; historical
                DoA rows referencing them stay valid.
              </div>
            </div>
          )}
        </div>

        <footer className="flex justify-end gap-2 border-t border-surface-border px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={!isDirty || save.isPending}
            className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </footer>

        <UnsavedChangesModal
          guard={unsavedGuard}
          title="Leave without saving?"
          body="You have unsaved responsibility selections. If you leave now, they will be lost."
          proceedLabel="Discard & leave"
          stayLabel="Stay and configure"
        />
      </div>
    </div>,
    document.body,
  );
}
